// The workspaces Me's things can belong to, as the screens draw them: one
// list per person, read once and shared by every row and every detail.
//
// Promise:
// - `connected` false (a Vault with no cloud to reach): the list is what was
//   last heard of (empty if never), so icons still show, but nothing can be
//   moved to a workspace.
// - A workspace the person left stays in the list (`member` false) with its
//   last name and icon: a thing tied to it still shows it.
// - Moving a thing refreshes the lists and details it can be on; nothing is
//   guessed ahead of the server.
import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { agentsPrefix } from "../agent-chat/queries";
import { useApi, useMeApp } from "../app/context";
import { useMeWorkspace } from "../app/workspace-filter";
import { casesPrefix } from "../cases/queries";
import type { Api } from "../data/api";
import { wholeFileUrl } from "../documents/blocks/upload";
import { documentsPrefix } from "../documents/queries";
import { personsPrefix } from "../persons/queries";

export interface WorkspaceMark {
  id: string;
  name: string;
  slug: string;
  /** The file id of its icon in the cloud. */
  imageId: string | null;
  /** False once the person left it. */
  member: boolean;
}

interface WorkspaceList {
  connected: boolean;
  items: WorkspaceMark[];
}

export const workspacesKey = ["me", "workspaces"] as const;

export const workspacesQuery = (api: Api) =>
  queryOptions({
    queryKey: workspacesKey,
    queryFn: () => api<WorkspaceList>("/api/me/workspaces"),
    staleTime: 60_000,
  });

export interface Workspaces {
  connected: boolean;
  byId: ReadonlyMap<string, WorkspaceMark>;
  /** The ones the person is in now. */
  members: readonly WorkspaceMark[];
  /** The icon's whole URL (the cloud's web draws it; elsewhere none). */
  imageOf(w: WorkspaceMark): string | null;
}

const NONE: WorkspaceList = { connected: false, items: [] };

export function useWorkspaces(): Workspaces {
  const api = useApi();
  const { data } = useQuery(workspacesQuery(api));
  const { apiBase, extensions } = useMeApp();
  const draws = extensions.personalPlace === "cloud";
  const list = Array.isArray(data?.items) ? data : NONE;
  return useMemo(
    () => ({
      connected: list.connected,
      byId: new Map(list.items.map((w) => [w.id, w])),
      members: list.items.filter((w) => w.member),
      imageOf: (w) =>
        draws && w.imageId ? wholeFileUrl(apiBase, w.imageId) : null,
    }),
    [list, draws, apiBase],
  );
}

/**
 * Whether lists draw a workspace's icon on each row: in Personal only. Narrowed
 * to one workspace every row is that workspace's.
 */
export function useShowRowWorkspaces(): boolean {
  return useMeWorkspace()[0] === null;
}

export type WorkspaceThing =
  | { kind: "case"; id: string }
  | { kind: "agent_chat"; id: string }
  | { kind: "page"; id: string }
  | { kind: "person"; id: string };

const PATHS: Record<WorkspaceThing["kind"], string> = {
  case: "/api/me/cases/",
  agent_chat: "/api/me/agents/chats/",
  page: "/api/me/pages/",
  person: "/api/me/persons/",
};

const refresh = (qc: QueryClient) =>
  Promise.all(
    [casesPrefix, agentsPrefix, documentsPrefix, personsPrefix].map((k) =>
      qc.invalidateQueries({ queryKey: k }),
    ),
  );

/**
 * Moves a thing to a workspace (`workspace` an id; null takes it out), or for
 * a person adds and takes out workspaces (`add`, `remove`).
 */
export function useMoveWorkspace(thing: WorkspaceThing) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | { workspace: string | null }
        | { add?: string[]; remove?: string[] },
    ) =>
      api(`${PATHS[thing.kind]}${encodeURIComponent(thing.id)}`, {
        method: "PATCH",
        body:
          "workspace" in input
            ? { workspace: input.workspace }
            : {
                ...(input.add?.length ? { add_workspaces: input.add } : {}),
                ...(input.remove?.length
                  ? { remove_workspaces: input.remove }
                  : {}),
              },
      }),
    onSettled: () => refresh(qc),
  });
}
